// Content data for Eco Sweeps LLC website

export const PDF_URL =
  "https://customer-assets.emergentagent.com/wingman/36a15b32-3e11-45f7-9097-6f8468f2ac94/attachments/e52c0416fa72455fae214f6036feb046_Eco_Sweeps_LLC_Capability_Statement.pdf";

export const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_site-preview-330/artifacts/umni8kzy_8fd39e458aaecaaa18d9391433a97ec0e080f8dd9707795aed41b4386327eff6.png";

export const NAV_LINKS = [
  { href: "#portfolio", label: "Portfolio" },
  { href: "#capabilities", label: "Capabilities" },
  { href: "#compliance", label: "Compliance" },
  { href: "#process", label: "Process" },
  { href: "#leadership", label: "Leadership" },
  { href: "#case-study", label: "Report" },
];

export const TRUST_MARKS = [
  "FedRAMP Aligned",
  "NIST SP 800-53 Rev.5",
  "NIST SP 800-171",
  "CMMC 2.0 Level 2",
  "StateRAMP",
  "FISMA Moderate",
  "CJIS Compliant",
  "HIPAA / HITECH",
  "ISO/IEC 27001",
  "DFARS 252.204-7012",
];

export const CAPABILITIES = [
  {
    id: "001",
    title: "Secure Cloud Migration",
    tag: "AWS GovCloud • Azure Gov",
    desc: "Lift-and-shift, replatform, and refactor operations for Delaware agencies transitioning from co-located legacy environments to authorized public sector cloud regions.",
    meta: ["Landing zones", "IL4/IL5", "Data classification"],
  },
  {
    id: "002",
    title: "Cybersecurity Engineering",
    tag: "Zero-Trust • SOC 2 Type II",
    desc: "Perimeter hardening, endpoint detection, identity federation, and continuous monitoring aligned to NIST 800-53 Rev.5 moderate control baseline.",
    meta: ["MFA / SSO", "SIEM tuning", "Threat hunt"],
  },
  {
    id: "003",
    title: "DevSecOps Enablement",
    tag: "CI/CD • Policy-as-Code",
    desc: "Pipeline modernization with automated security gates, IaC scanning, signed artifacts, and STIG-aligned base images across agency delivery workflows.",
    meta: ["GitOps", "SBOM", "OPA / Sentinel"],
  },
  {
    id: "004",
    title: "Data Modernization",
    tag: "Lakehouse • Governance",
    desc: "Consolidation of siloed record systems into governed lakehouse architectures with lineage, entitlements, and FOIA-ready audit trails.",
    meta: ["ETL/ELT", "Catalog", "PII tokenization"],
  },
  {
    id: "005",
    title: "Compliance & Continuous ATO",
    tag: "eMASS • OSCAL",
    desc: "Machine-readable control evidence, POA&M lifecycle management, and package curation supporting Authority to Operate on rolling review.",
    meta: ["OSCAL export", "POA&M", "3PAO liaison"],
  },
  {
    id: "006",
    title: "Legacy System Sunset",
    tag: "AS/400 • COBOL • Oracle",
    desc: "Documentation recovery, dependency mapping, and staged decommissioning of technical debt with archival strategies that satisfy retention statutes.",
    meta: ["Reverse eng.", "Archival", "Cut-over"],
  },
];

export const COMPLIANCE_MATRIX = [
  { name: "NIST SP 800-53 Rev.5", family: "Federal", controls: 421, status: "IMPLEMENTED" },
  { name: "NIST SP 800-171 Rev.3", family: "CUI", controls: 110, status: "IMPLEMENTED" },
  { name: "FedRAMP Moderate", family: "Cloud", controls: 323, status: "ALIGNED" },
  { name: "CMMC 2.0 Level 2", family: "DoD", controls: 110, status: "IMPLEMENTED" },
  { name: "StateRAMP", family: "State", controls: 325, status: "IN REVIEW" },
  { name: "FISMA Moderate", family: "Federal", controls: 261, status: "IMPLEMENTED" },
  { name: "CJIS Security Policy", family: "Law Enf.", controls: 90, status: "IMPLEMENTED" },
  { name: "HIPAA / HITECH", family: "Health", controls: 78, status: "IMPLEMENTED" },
  { name: "ISO/IEC 27001:2022", family: "Intl", controls: 93, status: "ALIGNED" },
  { name: "SOC 2 Type II", family: "Trust", controls: 64, status: "IN REVIEW" },
  { name: "DFARS 252.204-7012", family: "Defense", controls: 14, status: "IMPLEMENTED" },
  { name: "PCI-DSS v4.0", family: "Payment", controls: 306, status: "ALIGNED" },
];

export const PROCESS_PHASES = [
  {
    phase: "01",
    code: "ASSESS",
    title: "Discovery & Assessment",
    duration: "2 – 4 wk",
    deliverables: ["Systems inventory", "Dependency graph", "Risk register", "Rough-order magnitude"],
  },
  {
    phase: "02",
    code: "ARCH",
    title: "Target Architecture",
    duration: "3 – 6 wk",
    deliverables: ["Reference design", "Landing zone spec", "Control mapping", "Cost model"],
  },
  {
    phase: "03",
    code: "MIGRATE",
    title: "Phased Migration",
    duration: "12 – 24 wk",
    deliverables: ["Wave execution", "Cutover runbooks", "Data parity checks", "Rollback plans"],
  },
  {
    phase: "04",
    code: "VALIDATE",
    title: "Validation & ATO",
    duration: "4 – 8 wk",
    deliverables: ["Penetration test", "Control assessment", "POA&M closure", "ATO package"],
  },
  {
    phase: "05",
    code: "OPTIMIZE",
    title: "Sustainment",
    duration: "Ongoing",
    deliverables: ["FinOps tuning", "SLO reporting", "Continuous ATO", "Managed services"],
  },
];

export const TECH_STACK = [
  {
    group: "Cloud Platforms",
    items: ["AWS GovCloud (US)", "Azure Government", "Google Assured Workloads", "Oracle Gov Cloud"],
  },
  {
    group: "Security Tooling",
    items: ["CrowdStrike Falcon", "Splunk Enterprise", "Palo Alto Prisma", "Wiz", "Tenable"],
  },
  {
    group: "Infrastructure as Code",
    items: ["Terraform", "OpenTofu", "Ansible", "Packer", "Helm"],
  },
  {
    group: "Delivery Pipelines",
    items: ["GitHub Actions", "GitLab CI", "Jenkins", "ArgoCD", "Harness"],
  },
  {
    group: "Observability",
    items: ["Datadog", "New Relic", "Grafana", "Prometheus", "OpenTelemetry"],
  },
  {
    group: "Compliance Automation",
    items: ["OSCAL", "eMASS", "Regscale", "Chef InSpec", "OpenSCAP"],
  },
];

export const CASE_STUDY = {
  clientCode: "DE-AGY-04",
  classification: "PUBLIC RELEASE",
  summary:
    "A mid-sized Delaware public health agency operating a decade-old records platform on aging on-prem hardware, with recurring downtime and mounting audit findings.",
  before: [
    { label: "Uptime (rolling 30d)", value: "97.42%" },
    { label: "P1 incidents / mo", value: "11" },
    { label: "Annual infra spend", value: "$1.84M" },
    { label: "Open POA&M items", value: "63" },
  ],
  after: [
    { label: "Uptime (rolling 30d)", value: "99.987%" },
    { label: "P1 incidents / mo", value: "0.4" },
    { label: "Annual infra spend", value: "$0.71M" },
    { label: "Open POA&M items", value: "4" },
  ],
  outcomes: [
    "Achieved Continuous ATO on rolling 90-day review cycle",
    "Reduced mean-time-to-recover from 41 minutes to 3.2 minutes",
    "Cut annual infrastructure spend by 61.4% at higher throughput",
    "Closed 59 of 63 open POA&M findings within first two quarters",
  ],
};

export const TERMINAL_LINES = [
  { t: 200, kind: "cmd", text: "$ eco --auth wilmington.gov --profile agy04" },
  { t: 350, kind: "info", text: "[ok] mtls handshake · fips-140-3 cipher suite" },
  { t: 500, kind: "cmd", text: "$ eco migrate plan --wave 03 --region us-gov-east-1" },
  { t: 650, kind: "info", text: "[scan] 42 workloads · 218 secrets · 1,904 assets" },
  { t: 800, kind: "info", text: "[nist] mapping controls to 800-53 rev.5 baseline · moderate" },
  { t: 950, kind: "warn", text: "[hold] 2 legacy dependencies pending remediation" },
  { t: 1100, kind: "cmd", text: "$ eco migrate execute --wave 03 --dry-run=false" },
  { t: 1250, kind: "info", text: "[wave] provisioning landing zone · vpc-a1b2c3" },
  { t: 1400, kind: "info", text: "[data] copying 3.42TB · aes-256-gcm · integrity=ok" },
  { t: 1550, kind: "info", text: "[cutover] traffic shifted 5% > 25% > 100%" },
  { t: 1700, kind: "ok", text: "[done] wave 03 promoted · rollback armed · slo:99.99" },
];
